<template>
  <div>
    <Editor v-if="$store.state.rotator.activeUrl" :key="$store.state.rotator.activeUrl" :window="editorWindow" />

    <!-- iframe is the editor -->
    <iframe ref="iframe-0" id="iframe-0" v-visible="!$store.state.rotator.livePreview && activeIframe == 0" class="proxy-iframe"></iframe>
    <!-- preview is the page with SDK -->
    <iframe ref="preview-0" id="preview-0" v-visible="$store.state.rotator.livePreview && activeIframe == 0" class="proxy-iframe move-left"></iframe>

    <!-- iframe is the editor -->
    <iframe ref="iframe-1" id="iframe-1" v-visible="!$store.state.rotator.livePreview && activeIframe == 1" class="proxy-iframe move-left"></iframe>
    <!-- preview is the page with SDK -->
    <iframe ref="preview-1" id="preview-1" v-visible="$store.state.rotator.livePreview && activeIframe == 1" class="proxy-iframe move-left"></iframe>

    <!-- hidden frame for SDK detection -->
    <iframe ref="sdk" id="sdk" v-show="false" class="proxy-iframe"></iframe>
  </div>
</template>

<script>
const TIMEOUT   = 'Page took too long to load'
const INCORRECT = 'Page did not load correctly'
const INVALID   = 'The URL entered is invalid'
const UNKNOWN   = 'An unknown error occurred'
const RESET     = 'RESET'

import $ from 'jquery';
import Vue from 'vue';
import Editor from './Editor.vue';

export default {
  name: 'ProxyFrame',
  components: { Editor },

  data() {
    return {
      activeIframe      : 1,
      inactiveIframe    : 0,
      loaded            : 0,
      preloadTimer      : null,
      sdkDetectionTimer : null,
      sdkMessage        : null
    }
  },

  computed: {
    editorWindow() {
      return this.$refs['iframe-' + this.activeIframe].contentWindow
    },
    selectors() {
      return this.$store.getters.getSelectionJson()
    }
  },

  created() {
    window.addEventListener('message', this.handleEvent)
  },
  destroyed() {
    window.removeEventListener('message', this.handleEvent)
  },

  watch: {
    '$store.state.rotator.preloadUrl'(newVal, oldVal) {
      // Valid URL is not entered, stop the preload process
      clearTimeout(this.preloadTimer)
      
      // Keep track of frame load events
      this.loaded     = 0
      this.sdkMessage = null
        
      // Url is empty, clear out the preload
      if (newVal === false || !newVal.length) {
        this.handleEvent(this.createBaseEvent(this.$refs['iframe-' + this.inactiveIframe].contentWindow, 'preload', this.$store.state.rotator.preloadError || RESET))

      // URL is set, try to load it
      } else {
        // One frame for editing
        this.$refs['iframe-' + this.inactiveIframe].src = this.getPreloadUrl(newVal)
        // One for live preview
        this.$refs['preview-' + this.inactiveIframe].src = this.getPreloadUrl(newVal, true)
        
        this.preloadTimer = setTimeout(() => {
          this.handleEvent(this.createBaseEvent(this.$refs['iframe-' + this.inactiveIframe].contentWindow, 'preload', TIMEOUT))
        }, 10000);
      }
    },
    '$store.state.rotator.activeUrl'(newVal, oldVal) {
      // When activeUrl is set, it means whatever URL was last preloaded becomes our main URL
      // Swap active & inactive frames
      this.activeIframe     = this.activeIframe? 0: 1
      this.inactiveIframe   = this.activeIframe? 0: 1
      
      this.$store.dispatch('setPreloadSettings', { url: false, status: false, error: false })
      this.pauseAllMedia()
      this.updateSDK()
      
      // Replay SDK event from preloading phase, or use a reset event to kick off the process
      this.sdkMessage           = this.sdkMessage || this.createBaseEvent(null, 'sdk', RESET)
      this.sdkMessage.source    = this.$refs['sdk'].contentWindow
      this.handleEvent(this.sdkMessage)
    },
    '$store.state.rotator.sdkDetection'(newVal, oldVal) {
      if (newVal) {
        this.$refs['sdk'].src = this.getPreloadUrl(this.$store.state.rotator.activeUrl);

        this.sdkDetectionTimer = setTimeout(() => {
          this.handleEvent(this.createBaseEvent(this.$refs['sdk'].contentWindow, 'sdk', TIMEOUT))
        }, 10000);
      }
    },
    '$store.state.rotator.livePreview'(newVal, oldVal) {
      this.pauseAllMedia()
    },
    selectors() {
      this.updateSDK()
    }
  },

  methods: {
    createBaseEvent(source, type, message) {
      return {
        source    : source,
        data      : {
          source  : 'radius',
          event   : type,
          success : false,
          message : message || null,
          params  : {}
        }
      }
    },
    getUrlFragment(url) {
      return url.indexOf('?') == -1? '?': '&'
    },
    getPreloadUrl(url, livePreview = false) {
      let proxyUrl = process.env.PROXY + url + this.getUrlFragment(url) + 'lt=1'

      if (livePreview)
        proxyUrl += '&radius_sdk=' + this.$store.getters.sdkIdentifiers

      return proxyUrl
    },
    handleEvent(event) {
      // Ensure that this was a radius-bound event
      if (event === null || event.data === null || typeof event.data !== 'object' || event.data.source !== 'radius')
        return

      switch (event.data.event) {
      case 'preload':
        this.pauseAllMedia()
        this.handlePreload(event)
        break

      case 'sdk':
        this.handleSDKDetection(event)
        break

      default:
      }
    },
    handlePreload(event) {
      try {
        var isFromPreloadFrame = ['iframe-', 'preview-'].some((type) => {
          // Ensure message came from the preload frame
          if (event.source === this.$refs[type + this.inactiveIframe].contentWindow)
            return true
        })

        // Event is not from one of the frames we're concerned with
        if (!isFromPreloadFrame) {
          return

        // Wait for both frames to load before claiming success
        } if (event.data.success === true && ++this.loaded >= 2) {
          clearTimeout(this.preloadTimer)
          this.$store.commit('setPreloadingState', { status: false, error: false })

        // An error has occured
        } else if (event.data.success !== true) {
          throw event.data.message || UNKNOWN
        }

      } catch (err) {
        clearTimeout(this.preloadTimer)

        // Reset URL to stop loading additional content
        this.$refs['iframe-' + this.inactiveIframe].src = '';
        this.$refs['preview-' + this.inactiveIframe].src = '';
        
        this.$store.dispatch('setPreloadSettings', { url: false, status: false, error: err == RESET? false: err })
      }
    },
    handleSDKDetection(event) {
      try {
        // If SDK event fires from inactive frame, store it incase it becomes the active frame
        if (event.source === this.$refs['iframe-' + this.inactiveIframe].contentWindow)
          return this.sdkMessage = this.cloneEvent({}, event)
        
      } catch (err) {
        return
      }
      
      try {
        // SDK can fire from either SDK or Active Iframe
        if (event.source === this.$refs['iframe-' + this.activeIframe].contentWindow ||
            event.source === this.$refs['sdk'].contentWindow) {

          // Set template
          if (event.data.success && event.data.params && event.data.params.template_id)
            this.$store.dispatch('setTemplateId', event.data.params.template_id)
          
          // Set SDK
          if (event.data.success && event.data.params && event.data.params.rotator_id)
            this.$store.commit('sdkDetected', event.data.params)
          
          // Detect script location
          if (event.data.success && event.data.params && event.data.params.location)
            this.$store.commit('setScriptLocation', event.data.params.location)
          
          throw RESET
        
        } else if (event.source === this.$refs['preview-' + this.activeIframe].contentWindow) {
          this.updateSDK()
        }
        
        // Events from other contexts will fall through
        return
        
      } catch (err) {
        clearTimeout(this.sdkDetectionTimer)
        this.$store.commit('sdkDetection', false)
        this.$refs['sdk'].src = ''
      }
    },
    pauseAllMedia() {
      Object.values(this.$refs).forEach((frame) => {
        try {
          if (frame.contentDocument) {
            let elements = frame.contentDocument.querySelectorAll('audio,video,object')
            for (let i = 0; i < elements.length; i++)
              elements[i].pause()
          }
        } catch (err) {
          //
        }
      })
    },
    cloneEvent(target, source) {
      function EventCloneFactory(overProps){
        for(var x in overProps){
          this[x] = overProps[x];
        }
      }
      EventCloneFactory.prototype = target;
      return new EventCloneFactory(source);
    },
    updateSDK() {
      if (this.$store.state.rotator.activeUrl) {
        try {
          this.$refs['preview-' + this.activeIframe].contentWindow.jbo('selectors', JSON.parse(this.selectors))
        } catch (err) {
          //
        }
      }
    }
  }
}
</script>

<style scoped>
 .proxy-iframe {
   width: 100%;
   height: 100%;
   min-width: 360px;
   min-height: 360px;
   overflow: auto;
   position: relative;
   float: left;
 }
 .move-left {
   margin-left: -100%;
 }
</style>



// WEBPACK FOOTER //
// ProxyFrame.vue?9150451a